import { ArrowLeft, Apple, Sparkles, ShieldCheck, Star, CheckCircle, Download, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { APP_STORE_URL } from '@/lib/appStoreUrl';

/**
 * ClayPulse iOS landing page — now that the app is live on the App Store,
 * this page celebrates the launch and pushes visitors to install directly
 * from the store. The previous PWA install flow is retired.
 */
export default function IOSInstall() {
  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <Link
          to="/"
          data-testid="ios-back-link"
          className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#FF6B35] text-sm mb-10 transition-colors"
        >
          <ArrowLeft size={16} />
          Retour à l&apos;accueil
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#22C55E]/10 border border-[#22C55E]/30 mb-5">
            <Sparkles size={13} className="text-[#22C55E]" />
            <span className="text-[#22C55E] text-[11px] font-bold uppercase tracking-[0.2em]">
              Nouveau · Disponible sur l&apos;App Store
            </span>
          </div>

          <div className="flex items-start gap-4">
            <img
              src="/assets/img/logo-claypulse.jpg"
              alt="ClayPulse"
              className="w-20 h-20 rounded-2xl object-cover shrink-0"
            />
            <div>
              <h1 className="font-['Poppins'] font-black text-3xl sm:text-4xl leading-tight mb-2">
                ClayPulse est sur l&apos;App Store
              </h1>
              <div className="flex items-center gap-1 text-[#A0A0A0] text-sm">
                <Apple size={14} />
                <span>Version officielle iOS</span>
              </div>
            </div>
          </div>
        </motion.header>

        {/* Main CTA */}
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          data-testid="ios-appstore-cta-section"
          className="relative rounded-3xl bg-gradient-to-br from-[#1a1a1a] to-[#0D0D0D] border border-white/10 p-7 sm:p-9 mb-8 overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#FF6B35]/12 rounded-full blur-3xl pointer-events-none" />

          <div className="relative text-center">
            <h2 className="font-['Poppins'] font-bold text-2xl sm:text-3xl mb-3">
              Télécharge la version{' '}
              <span className="bg-gradient-to-r from-[#FFC107] to-[#FF6B35] bg-clip-text text-transparent">
                officielle
              </span>
            </h2>
            <p className="text-[#C8C8C8] text-sm sm:text-base mb-8 max-w-xl mx-auto">
              L&apos;application ClayPulse est désormais disponible sur l&apos;App Store.
              Téléchargement gratuit, mises à jour automatiques et expérience iOS native.
            </p>

            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ios-appstore-cta"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-white text-black font-bold text-base hover:scale-105 hover:shadow-[0_0_40px_rgba(255,255,255,0.25)] transition-all duration-300"
            >
              <Apple size={24} />
              <div className="text-left">
                <div className="text-[10px] font-medium leading-none opacity-70 uppercase tracking-wider">
                  Télécharger dans
                </div>
                <div className="text-base font-bold leading-tight">l&apos;App Store</div>
              </div>
              <ExternalLink size={16} className="opacity-50 ml-1" />
            </a>

            <p className="text-[#666] text-[11px] mt-5 flex items-center justify-center gap-1.5">
              <ShieldCheck size={11} />
              iOS 15 et supérieur · 7 jours d&apos;essai gratuit
            </p>
          </div>
        </motion.section>

        {/* What's new / benefits */}
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="grid sm:grid-cols-2 gap-3 mb-8"
        >
          {[
            { icon: Star, title: "Version officielle stable", desc: "Optimisée pour iOS. Zéro bug. Mises à jour automatiques via l'App Store." },
            { icon: Download, title: "Installation en 1 clic", desc: "Plus besoin de PWA ni de configuration : directement depuis l'App Store." },
            { icon: ShieldCheck, title: "Sécurisée par Apple", desc: "Validée par le processus de review Apple. Paiements gérés par Apple." },
            { icon: CheckCircle, title: "Récupération des comptes", desc: "Connecte-toi avec ton email existant, tes données sont conservées." },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="p-5 rounded-xl bg-white/[0.03] border border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={17} className="text-[#FF6B35]" />
                  <span className="text-white font-semibold text-sm">{item.title}</span>
                </div>
                <p className="text-[#A0A0A0] text-xs leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </motion.section>

        {/* Existing PWA users note */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="p-5 rounded-xl bg-[#22C55E]/8 border border-[#22C55E]/20 mb-8"
          data-testid="ios-existing-users-note"
        >
          <div className="flex items-start gap-3">
            <ShieldCheck size={18} className="text-[#22C55E] shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-semibold text-sm mb-1">
                Tu utilisais déjà la version PWA&nbsp;?
              </h3>
              <p className="text-[#C8C8C8] text-xs sm:text-sm leading-relaxed">
                Télécharge la nouvelle version App Store et connecte-toi avec ton
                email : ton abonnement et toutes tes données seront automatiquement
                récupérés. Une question ?{' '}
                <a
                  href="mailto:supportclaypulse@gmail.com"
                  className="text-[#FF6B35] hover:underline font-semibold"
                >
                  supportclaypulse@gmail.com
                </a>
              </p>
            </div>
          </div>
        </motion.section>

        {/* Android fallback */}
        <motion.section
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="text-center"
        >
          <p className="text-[#666] text-xs mb-2">
            Tu es sur Android&nbsp;?
          </p>
          <Link
            to="/android"
            data-testid="ios-fallback-android"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-white/[0.03] text-white text-sm font-semibold hover:bg-white/[0.06] hover:border-[#FF6B35]/30 transition-all"
          >
            Voir la version Android (APK)
          </Link>
        </motion.section>
      </div>
    </div>
  );
}
